import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Helmet } from "react-helmet";
import { motion, AnimatePresence } from "motion/react";
import { toast } from '../components/ui/notify';
import {
  ArrowDownCircle,
  Clock,
  CreditCard,
  Gift,
  MapPin,
  Phone,
  Plus,
  Search,
  Trash2,
  TrendingUp,
  User,
  X,
} from "lucide-react";

import AccessDenied from "../components/AccessDenied";
import DontActiveAcount from "../components/DontActiveAcount";
import { base_url } from "../api/index";
import { PosLoader } from "../components/pos/PosLoader";
import { useI18n, tStatic } from "../i18n/I18nContext";
import { useConfirm } from "../components/ConfirmDialog";

const getHeaders = () => ({
  headers: {
    Authorization: `Bearer ${localStorage.getItem("token")}`,
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

const unwrapCustomers = (payload: any): any[] => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.customers)) return payload.customers;
  if (Array.isArray(payload?.data?.customers)) return payload.data.customers;
  return [];
};

const money = (value: any) => Number(value ?? 0) || 0;

const getBalance = (customer: any) => money(customer.balance ?? customer.money);
const getDebt = (customer: any) => {
  const explicitDebt = customer.debt ?? customer.total_debt;
  if (explicitDebt != null) return Math.max(0, money(explicitDebt));
  return Math.max(0, -getBalance(customer));
};
const getPositiveBalance = (customer: any) => Math.max(0, getBalance(customer));

const formatLastVisit = (value: any) => {
  if (!value) return tStatic("cust.noVisit");
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return String(value);
  return date.toLocaleDateString("tr-TR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

function Musteriler() {
  const { t } = useI18n();
  const { confirm, confirmDialog } = useConfirm();
  const [customers, setCustomers] = useState<any[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<any | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  const [activeUser, setActiveUser] = useState(false);
  const [showAddCustomer, setShowAddCustomer] = useState(false);
  const [showAddBalance, setShowAddBalance] = useState(false);
  const [balanceAmount, setBalanceAmount] = useState("");
  // "withdraw" = hesabdan pul çıxarma. Eyni panel iki rejimdə işləyir ki,
  // ekranda ikinci, demək olar eyni forma olmasın.
  const [balanceMode, setBalanceMode] = useState<"add" | "withdraw">("add");
  const [newCustomer, setNewCustomer] = useState({
    name: "",
    phone: "",
    address: "",
  });

  const handleApiError = (error: any, fallback = t("cust.opFailed")) => {
    if (
      error.response?.status === 403 &&
      error.response?.data?.message ===
        "User does not belong to any  active restaurant."
    ) {
      setActiveUser(true);
      return;
    }
    if (error.response?.status === 403) {
      setAccessDenied(true);
      return;
    }
    // Müştəri hesabı YALNIZ buludda yaşayır (pos_local-da `customers`
    // cədvəli yoxdur), ona görə bu ekran internetsiz işləyə bilmir.
    // `error.response` yoxdursa sorğu ümumiyyətlə çatmayıb.
    if (!error.response) {
      toast.error(t("cust.offline"));
      return;
    }
    toast.error(error.response?.data?.message || fallback);
  };

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${base_url}/customers`, getHeaders());
      const list = unwrapCustomers(response.data);
      setCustomers(list);

      const savedId = localStorage.getItem("selectedCustomerId");
      const nextSelected = savedId
        ? list.find((customer) => String(customer.id) === String(savedId))
        : list[0];
      setSelectedCustomer(nextSelected || null);
    } catch (error: any) {
      handleApiError(error, t("cust.loadFailed"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, []);

  const filteredCustomers = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return customers;
    return customers.filter((customer) => {
      const haystack = [
        customer.name,
        customer.phone,
        customer.address,
        customer.email,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [customers, searchQuery]);

  const stats = useMemo(() => {
    const totalBalance = customers.reduce(
      (sum, customer) => sum + getPositiveBalance(customer),
      0,
    );
    const totalDebt = customers.reduce(
      (sum, customer) => sum + getDebt(customer),
      0,
    );
    const loyaltyPoints = customers.reduce(
      (sum, customer) =>
        sum + money(customer.loyalty_points ?? customer.loyaltyPoints),
      0,
    );
    return {
      total: customers.length,
      withBalance: customers.filter(
        (customer) => getPositiveBalance(customer) > 0,
      ).length,
      withDebt: customers.filter((customer) => getDebt(customer) > 0).length,
      totalBalance,
      totalDebt,
      loyaltyPoints,
    };
  }, [customers]);

  const selectCustomer = (customer: any) => {
    setSelectedCustomer(customer);
    localStorage.setItem("selectedCustomerId", String(customer.id));
    setShowAddBalance(false);
  };

  const handleDeleteCustomer = async (
    customer: any,
    event: React.MouseEvent,
  ) => {
    event.stopPropagation();
    const ok = await confirm({
      message: t("cust.deleteConfirm", { name: customer.name }),
      confirmLabel: t("common.delete"),
      variant: "danger",
    });
    if (!ok) return;
    try {
      await axios.delete(`${base_url}/customers/${customer.id}`, getHeaders());
      setCustomers((prev) => prev.filter((item) => item.id !== customer.id));
      if (selectedCustomer?.id === customer.id) {
        setSelectedCustomer(null);
        localStorage.removeItem("selectedCustomerId");
      }
      toast.success(t("cust.deleted"));
    } catch (error: any) {
      handleApiError(error, t("cust.deleteFailed"));
    }
  };

  const handleAddCustomer = async () => {
    if (!newCustomer.name.trim() || !newCustomer.phone.trim()) {
      toast.warning(t("cust.nameePhoneRequired"));
      return;
    }

    try {
      setSaving(true);
      const response = await axios.post(
        `${base_url}/customers`,
        {
          name: newCustomer.name.trim(),
          phone: newCustomer.phone.trim(),
          address: newCustomer.address.trim() || undefined,
        },
        getHeaders(),
      );
      const created =
        response.data?.customer || response.data?.data || response.data;
      if (created?.id) {
        setCustomers((prev) => [created, ...prev]);
        selectCustomer(created);
      } else {
        await fetchCustomers();
      }
      setShowAddCustomer(false);
      setNewCustomer({ name: "", phone: "", address: "" });
      toast.success(t("cust.added"));
    } catch (error: any) {
      handleApiError(error, t("cust.addFailed"));
    } finally {
      setSaving(false);
    }
  };

  const handleAddBalance = async () => {
    if (!selectedCustomer || !balanceAmount) {
      toast.warning(t("cust.enterAmount"));
      return;
    }

    const amount = Number(balanceAmount);
    if (!Number.isFinite(amount) || amount <= 0) {
      toast.warning(t("cust.amountPositive"));
      return;
    }

    const isWithdraw = balanceMode === "withdraw";

    // Çıxarışda qalığı əvvəlcədən yoxlayırıq ki, istifadəçi serverə getmədən
    // xəbərdarlıq alsın. Əsl qoruma yenə serverdədir (atomik yoxlama).
    if (isWithdraw && amount > getBalance(selectedCustomer)) {
      toast.warning(t("cust.insufficientBalance"));
      return;
    }

    try {
      setSaving(true);

      // ƏVVƏL burada 404 halında `customers/:id/transaction` ünvanına
      // "ehtiyat" sorğu gedirdi — belə bir endpoint HEÇ VAXT OLMAYIB.
      // İndi hər iki əməliyyatın öz ünvanı var.
      await axios.post(
        `${base_url}/customers/${selectedCustomer.id}/${isWithdraw ? "withdraw" : "add-balance"}`,
        {
          amount,
          description: isWithdraw ? t("cust.posWithdraw") : t("cust.posTopup"),
        },
        getHeaders(),
      );

      const nextBalance = getBalance(selectedCustomer) + (isWithdraw ? -amount : amount);
      setCustomers((prev) =>
        prev.map((customer) =>
          customer.id === selectedCustomer.id
            ? { ...customer, balance: nextBalance, money: nextBalance }
            : customer,
        ),
      );
      setSelectedCustomer((prev) =>
        prev ? { ...prev, balance: nextBalance, money: nextBalance } : prev,
      );
      setShowAddBalance(false);
      setBalanceAmount("");
      toast.success(isWithdraw ? t("cust.balanceWithdrawn") : t("cust.balanceLoaded"));
    } catch (error: any) {
      handleApiError(
        error,
        isWithdraw ? t("cust.withdrawFailed") : t("cust.balanceFailed"),
      );
    } finally {
      setSaving(false);
    }
  };

  if (accessDenied)
    return <AccessDenied onClose={() => setAccessDenied(false)} />;
  if (activeUser) return <DontActiveAcount sil={setActiveUser} />;

  return (
    <div className="h-[calc(100vh-5rem)] overflow-y-auto p-6 space-y-6 bg-gray-50 dark:bg-[#0f0f1a]">
      <Helmet>
        <title>{t("nav.customers")} | Smartcafe</title>
        <meta
          name="description"
          content={t("masalar.pageDesc")}
        />
      </Helmet>
      {/* Toast: App-dəki tək qlobal ToastContainer istifadə olunur. */}

      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold mb-1 text-gray-900 dark:text-white">
            {t("cust.title")}
          </h1>
          <p className="text-gray-500 dark:text-gray-400">
            {t("cust.summary", { count: stats.total, balance: stats.totalBalance.toFixed(2), debt: stats.totalDebt.toFixed(2) })}
          </p>
        </div>

        <button
          onClick={() => setShowAddCustomer(true)}
          className="px-5 py-2.5 bg-[var(--primary)] text-white rounded-lg hover:bg-[var(--primary)]/90 transition-all flex items-center gap-2 font-semibold shadow-lg shadow-orange-200 dark:shadow-orange-900/50"
        >
          <Plus className="w-4 h-4" />
          {t("cust.new")}
        </button>
      </div>

      <AnimatePresence>
        {showAddCustomer && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowAddCustomer(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.96, opacity: 0 }}
              onClick={(event) => event.stopPropagation()}
              className="bg-white dark:bg-[#1a1a2e] rounded-2xl p-6 max-w-md w-full shadow-2xl border border-gray-100 dark:border-gray-800"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                  {t("cust.addTitle")}
                </h2>
                <button
                  onClick={() => setShowAddCustomer(false)}
                  className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">
                    {t("cust.fullName")}
                  </label>
                  <input
                    type="text"
                    value={newCustomer.name}
                    onChange={(event) =>
                      setNewCustomer({
                        ...newCustomer,
                        name: event.target.value,
                      })
                    }
                    placeholder={t("cust.namePlaceholder")}
                    className="w-full px-4 py-3 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-[var(--primary)] bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder:text-gray-400"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">
                    {t("cust.phoneRequired")}
                  </label>
                  <input
                    type="tel"
                    value={newCustomer.phone}
                    onChange={(event) =>
                      setNewCustomer({
                        ...newCustomer,
                        phone: event.target.value,
                      })
                    }
                    placeholder="050 123 45 67"
                    className="w-full px-4 py-3 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-[var(--primary)] bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder:text-gray-400"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">
                    {t("cust.addressOptional")}
                  </label>
                  <textarea
                    value={newCustomer.address}
                    onChange={(event) =>
                      setNewCustomer({
                        ...newCustomer,
                        address: event.target.value,
                      })
                    }
                    placeholder={t("cust.addressPlaceholder")}
                    rows={3}
                    className="w-full px-4 py-3 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-[var(--primary)] bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder:text-gray-400"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 mt-6">
                <button
                  onClick={() => setShowAddCustomer(false)}
                  className="px-5 py-3 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-700 transition-all font-semibold"
                >
                  {t("common.cancel")}
                </button>
                <button
                  onClick={handleAddCustomer}
                  disabled={saving}
                  className="px-5 py-3 bg-[var(--primary)] text-white rounded-xl hover:bg-[var(--primary)]/90 transition-all font-semibold disabled:opacity-60"
                >
                  {saving ? t("cust.adding") : t("common.add")}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-[#1a1a2e] rounded-xl p-6 border border-gray-100 dark:border-gray-800">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">
                {t("cust.stat.total")}
              </p>
              <p className="text-3xl font-bold text-gray-900 dark:text-white">
                {stats.total}
              </p>
            </div>
            <div className="w-12 h-12 rounded-lg bg-[var(--primary)]/10 flex items-center justify-center">
              <User className="w-6 h-6 text-[var(--primary)]" />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-[#1a1a2e] rounded-xl p-6 border border-gray-100 dark:border-gray-800">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">
                {t("cust.stat.withBalance")}
              </p>
              <p className="text-3xl font-bold text-[var(--success)]">
                {stats.withBalance}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                ₼{stats.totalBalance.toFixed(2)}
              </p>
            </div>
            <div className="w-12 h-12 rounded-lg bg-[var(--success)]/10 flex items-center justify-center">
              <CreditCard className="w-6 h-6 text-[var(--success)]" />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-[#1a1a2e] rounded-xl p-6 border border-gray-100 dark:border-gray-800">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">
                {t("cust.stat.withDebt")}
              </p>
              <p className="text-3xl font-bold text-red-500">
                {stats.withDebt}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                ₼{stats.totalDebt.toFixed(2)}
              </p>
            </div>
            <div className="w-12 h-12 rounded-lg bg-red-500/10 flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-red-500" />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-[#1a1a2e] rounded-xl p-6 border border-gray-100 dark:border-gray-800">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">
                {t("cust.loyalty")}
              </p>
              <p className="text-3xl font-bold text-[var(--secondary)]">
                {stats.loyaltyPoints}
              </p>
            </div>
            <div className="w-12 h-12 rounded-lg bg-[var(--secondary)]/10 flex items-center justify-center">
              <Gift className="w-6 h-6 text-[var(--secondary)]" />
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
            <input
              type="text"
              placeholder={t("cust.searchPlaceholder")}
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              className="w-full pl-11 pr-4 py-2.5 bg-white dark:bg-[#1a1a2e] border border-gray-100 dark:border-gray-800 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--primary)] text-sm text-gray-900 dark:text-white placeholder:text-gray-400"
            />
          </div>

          {loading ? (
            <div className="py-20 flex items-center justify-center">
              <PosLoader size="lg" label={t("cust.loading")} />
            </div>
          ) : (
            <div className="space-y-3">
              <AnimatePresence mode="popLayout">
                {filteredCustomers.map((customer) => {
                  const balance = getPositiveBalance(customer);
                  const debt = getDebt(customer);
                  const loyalty = money(
                    customer.loyalty_points ?? customer.loyaltyPoints,
                  );
                  return (
                    <motion.div
                      key={customer.id}
                      layout
                      onClick={() => selectCustomer(customer)}
                      className={`bg-white dark:bg-[#1a1a2e] rounded-xl p-5 border hover:shadow-md transition-all cursor-pointer ${
                        selectedCustomer?.id === customer.id
                          ? "border-[var(--primary)] ring-2 ring-[var(--primary)]/20 shadow-md"
                          : "border-gray-100 dark:border-gray-800"
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-4 flex-1">
                          <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[var(--primary)] to-[var(--warning)] flex items-center justify-center text-white font-bold text-lg shrink-0">
                            {String(customer.name || "?")
                              .charAt(0)
                              .toUpperCase()}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-bold mb-2 text-gray-900 dark:text-white">
                              {customer.name || t("cust.unnamed")}
                            </h3>
                            <div className="space-y-1 text-sm text-gray-500 dark:text-gray-400">
                              {customer.phone && (
                                <div className="flex items-center gap-2">
                                  <Phone className="w-3.5 h-3.5" />
                                  <span>{customer.phone}</span>
                                </div>
                              )}
                              {customer.address && (
                                <div className="flex items-center gap-2">
                                  <MapPin className="w-3.5 h-3.5" />
                                  <span className="truncate">
                                    {customer.address}
                                  </span>
                                </div>
                              )}
                              <div className="flex items-center gap-2">
                                <Clock className="w-3.5 h-3.5" />
                                <span>
                                  {formatLastVisit(
                                    customer.last_visit ?? customer.updated_at,
                                  )}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="text-right space-y-2 ml-4">
                          {balance > 0 && (
                            <div className="px-3 py-1 bg-[var(--success)]/10 text-[var(--success)] rounded-lg text-sm font-semibold">
                              +₼{balance.toFixed(2)}
                            </div>
                          )}
                          {debt > 0 && (
                            <div className="px-3 py-1 bg-red-500/10 text-red-500 rounded-lg text-sm font-semibold">
                              -₼{debt.toFixed(2)}
                            </div>
                          )}
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {t("cust.ordersCount", { n: customer.total_orders ?? customer.totalOrders ?? 0 })}
                          </p>
                          <button
                            onClick={(event) =>
                              handleDeleteCustomer(customer, event)
                            }
                            className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 transition-all"
                            title={t("common.delete")}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>

                      <div className="mt-4 p-3 bg-[var(--secondary)]/5 dark:bg-[var(--secondary)]/10 rounded-lg border border-[var(--secondary)]/20 dark:border-[var(--secondary)]/30">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <Gift className="w-3.5 h-3.5 text-[var(--secondary)]" />
                            <span className="text-sm font-semibold text-gray-900 dark:text-white">
                              {t("cust.loyalty")}
                            </span>
                          </div>
                          <span className="font-bold text-[var(--secondary)]">
                            {loyalty}
                          </span>
                        </div>
                        <div className="h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-[var(--secondary)] rounded-full transition-all"
                            style={{
                              width: `${Math.min((loyalty / 1000) * 100, 100)}%`,
                            }}
                          />
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </AnimatePresence>

              {filteredCustomers.length === 0 && (
                <div className="py-16 text-center text-gray-500 dark:text-gray-400 bg-white dark:bg-[#1a1a2e] rounded-xl border border-gray-100 dark:border-gray-800">
                  {t("cust.notFound")}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="space-y-4">
          {selectedCustomer ? (
            <>
              <div className="bg-white dark:bg-[#1a1a2e] rounded-xl p-5 border border-gray-100 dark:border-gray-800">
                <h2 className="text-lg font-bold mb-4 text-gray-900 dark:text-white">
                  {t("cust.detail")}
                </h2>
                <div className="mb-5 flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[var(--primary)] to-[var(--warning)] flex items-center justify-center text-white font-black">
                    {String(selectedCustomer.name || "?")
                      .charAt(0)
                      .toUpperCase()}
                  </div>
                  <div>
                    <p className="font-bold text-gray-900 dark:text-white">
                      {selectedCustomer.name}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {selectedCustomer.phone || t("cust.noPhone")}
                    </p>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="p-4 bg-[var(--success)]/5 dark:bg-[var(--success)]/10 rounded-lg border border-[var(--success)]/20">
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">
                      {t("cust.balance")}
                    </p>
                    <p className="text-2xl font-bold text-[var(--success)]">
                      ₼{getPositiveBalance(selectedCustomer).toFixed(2)}
                    </p>
                  </div>
                  <div className="p-4 bg-red-500/5 dark:bg-red-500/10 rounded-lg border border-red-500/20">
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">
                      {t("cust.debt")}
                    </p>
                    <p className="text-2xl font-bold text-red-500">
                      ₼{getDebt(selectedCustomer).toFixed(2)}
                    </p>
                  </div>
                  <div className="p-4 bg-[var(--secondary)]/5 dark:bg-[var(--secondary)]/10 rounded-lg border border-[var(--secondary)]/20">
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">
                      {t("cust.loyalty")}
                    </p>
                    <p className="text-2xl font-bold text-[var(--secondary)]">
                      {money(
                        selectedCustomer.loyalty_points ??
                          selectedCustomer.loyaltyPoints,
                      )}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2 mt-4">
                  <button
                    onClick={() => {
                      setBalanceMode("add");
                      setBalanceAmount("");
                      setShowAddBalance(true);
                    }}
                    className="px-4 py-3 bg-[var(--primary)] text-white rounded-lg hover:bg-[var(--primary)]/90 transition-all flex items-center justify-center gap-2 font-semibold"
                  >
                    <CreditCard className="w-4 h-4" />
                    {t("cust.loadBalance")}
                  </button>
                  {/* Hesabdan pul çıxarma — nağd qaytarma və ya düzəliş.
                      Qalıq sıfırdırsa düymə mənasızdır. */}
                  <button
                    onClick={() => {
                      setBalanceMode("withdraw");
                      setBalanceAmount("");
                      setShowAddBalance(true);
                    }}
                    disabled={getPositiveBalance(selectedCustomer) <= 0}
                    className="px-4 py-3 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-all flex items-center justify-center gap-2 font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <ArrowDownCircle className="w-4 h-4" />
                    {t("cust.withdrawBalance")}
                  </button>
                </div>
              </div>

              <AnimatePresence>
                {showAddBalance && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    className="bg-white dark:bg-[#1a1a2e] rounded-xl p-5 border-2 border-[var(--primary)] shadow-lg"
                  >
                    <h3 className="font-bold mb-1 text-gray-900 dark:text-white">
                      {balanceMode === "withdraw"
                        ? t("cust.withdrawBalance")
                        : t("cust.loadBalance")}
                    </h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                      {t("cust.currentBalance")}:{" "}
                      <span className="font-bold">
                        ₼{getBalance(selectedCustomer).toFixed(2)}
                      </span>
                    </p>
                    <input
                      type="number"
                      value={balanceAmount}
                      onChange={(event) => setBalanceAmount(event.target.value)}
                      placeholder={t("cust.enterAmountPlaceholder")}
                      className="w-full px-4 py-3 border border-gray-200 dark:border-gray-700 rounded-lg text-lg font-bold mb-4 focus:outline-none focus:ring-2 focus:ring-[var(--primary)] bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder:text-gray-400"
                    />
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        onClick={() => setShowAddBalance(false)}
                        className="px-5 py-3 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-all font-semibold"
                      >
                        {t("common.cancel")}
                      </button>
                      <button
                        onClick={handleAddBalance}
                        disabled={saving}
                        className={`px-5 py-3 text-white rounded-lg transition-all font-semibold disabled:opacity-60 ${
                          balanceMode === "withdraw"
                            ? "bg-[var(--danger,#dc3545)] hover:opacity-90"
                            : "bg-[var(--success)] hover:bg-[var(--success)]/90"
                        }`}
                      >
                        {saving
                          ? t("cust.loading2")
                          : balanceMode === "withdraw"
                            ? t("cust.withdraw")
                            : t("cust.load")}
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </>
          ) : (
            <div className="bg-white dark:bg-[#1a1a2e] rounded-xl p-12 border border-gray-100 dark:border-gray-800 text-center">
              <User className="w-16 h-16 mx-auto mb-4 text-gray-400 dark:text-gray-500" />
              <p className="text-gray-500 dark:text-gray-400">
                {t("cust.selectPrompt")}
              </p>
            </div>
          )}
        </div>
      </div>
      {confirmDialog}
    </div>
  );
}

export default Musteriler;
